import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

function fmt(t) {
  const [h,m] = t.split(':').map(Number);
  return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`;
}

function fmtBookedOn(ts) {
  if (!ts) return '—';
  const d = new Date(ts.replace(' ', 'T') + 'Z');
  const g = new Date(d.getTime() + 2 * 60 * 60 * 1000);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${g.getUTCDate()} ${months[g.getUTCMonth()]}, ${String(g.getUTCHours()).padStart(2,'0')}:${String(g.getUTCMinutes()).padStart(2,'0')}`;
}

const ST = {
  confirmed: { color:'#15803d', bg:'rgba(16,185,129,0.08)', dot:'#10b981' },
  pending:   { color:'#92400e', bg:'rgba(245,158,11,0.08)', dot:'#f59e0b' },
  cancelled: { color:'#64748b', bg:'rgba(148,163,184,0.08)', dot:'#94a3b8' },
  no_show:   { color:'#7c3aed', bg:'rgba(124,58,237,0.08)', dot:'#7c3aed' },
};

export default function History() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date:'', status:'', source:'', search:'' });
  const [sortBy, setSortBy]   = useState('date'); // 'date' | 'booked_on'
  const [sel, setSel]         = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = Object.fromEntries(Object.entries(filters).filter(([,v])=>v));
      const all = await api.getReservations(p);
      setRows(all);
    } catch(_){} finally { setLoading(false); }
  }, [filters]);

  useEffect(()=>{ load(); },[load]);
  useEffect(()=>{
    const refresh = () => load();
    window.addEventListener('seva:refresh', refresh);
    return ()=>window.removeEventListener('seva:refresh', refresh);
  },[load]);

  const open = async r => {
    const full = await api.getReservation(r.id);
    setSel(full);
  };

  const setF = (k,v) => setFilters(f=>({...f,[k]:v}));

  // Sort the rows
  const sorted = [...rows].sort((a, b) => {
    if (sortBy === 'booked_on') {
      return (b.created_at || '').localeCompare(a.created_at || '');
    }
    // Sort by reservation date + time (ascending: upcoming first, then past at bottom)
    const da = a.date + 'T' + a.time_slot;
    const db2 = b.date + 'T' + b.time_slot;
    return da.localeCompare(db2);
  });

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>History</h1>
          <p style={s.sub}>All reservations — past and upcoming.</p>
        </div>
      </div>

      <div style={s.filterBar}>
        <input style={s.fi} className="glass-input" type="date" value={filters.date} onChange={e=>setF('date',e.target.value)} />
        <select style={s.fi} className="glass-input" value={filters.status} onChange={e=>setF('status',e.target.value)}>
          <option value="">All statuses</option>
          <option value="confirmed">Confirmed</option>
          <option value="pending">Pending</option>
          <option value="cancelled">Cancelled</option>
          <option value="no_show">No-show</option>
        </select>
        <select style={s.fi} className="glass-input" value={filters.source} onChange={e=>setF('source',e.target.value)}>
          <option value="">All sources</option>
          <option value="ai">AI chatbot</option>
          <option value="manual">Manual</option>
        </select>
        <input style={{...s.fi,flex:1}} className="glass-input" type="text" placeholder="Search name or phone…" value={filters.search} onChange={e=>setF('search',e.target.value)} />
        {Object.values(filters).some(Boolean) && <button style={s.clearBtn} onClick={()=>setFilters({date:'',status:'',source:'',search:''})}>Clear</button>}
      </div>

      <div style={s.sortBar}>
        <span style={s.sortLabel}>Sort by:</span>
        <button
          style={{...s.sortBtn, ...(sortBy==='date' ? s.sortBtnActive : {})}}
          onClick={()=>setSortBy('date')}
        >Reservation date</button>
        <button
          style={{...s.sortBtn, ...(sortBy==='booked_on' ? s.sortBtnActive : {})}}
          onClick={()=>setSortBy('booked_on')}
        >Date booked</button>
      </div>

      {loading ? <div style={{color:'var(--text-muted)',padding:20}}>Loading…</div>
      : sorted.length === 0 ? (
        <div style={s.empty}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom:10}}>
            <path d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
          </svg>
          <div>No reservations found</div>
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>{['Date','Time','Name','Party','Table','Status','Source','Booked on','Confirmation'].map(h=>(
                <th key={h} style={s.th}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {sorted.map(r=>(
                <tr
                  key={r.id}
                  onClick={()=>open(r)}
                  className="glass-tr-hover"
                  style={['cancelled','no_show'].includes(r.status) ? {opacity:0.5} : {}}
                >
                  <td style={s.td}>{r.date}</td>
                  <td style={s.td}>{fmt(r.time_slot)}</td>
                  <td style={{...s.td,fontWeight:700,color:'var(--text-primary)'}}>{r.customer_name}</td>
                  <td style={s.td}>{r.party_size}</td>
                  <td style={s.td}>{r.tables_display || r.table_label}</td>
                  <td style={s.td}>
                    <span style={{...s.badge,color:(ST[r.status]||ST.confirmed).color,background:(ST[r.status]||ST.confirmed).bg}}>
                      <span style={{...s.dot,background:(ST[r.status]||ST.confirmed).dot}} />
                      {r.status.replace('_',' ')}
                    </span>
                  </td>
                  <td style={s.td}>
                    {r.source==='ai'?<span style={s.aiTag}>AI</span>:<span style={s.manTag}>Manual</span>}
                    {r.special_occasion && <span style={s.occasionTag} title={r.special_occasion}> 🎉</span>}
                  </td>
                  <td style={{...s.td,fontFamily:'monospace',fontSize:11,color:'var(--text-muted)'}}>{fmtBookedOn(r.created_at)}</td>
                  <td style={{...s.td,fontFamily:'monospace',fontSize:11,color:'var(--text-muted)'}}>{r.confirmation_number}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sel && (
        <div style={s.overlay} onClick={()=>setSel(null)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.modalTop}>
              <div>
                <h2 style={s.modalName}>{sel.customer_name}</h2>
                <div style={s.modalSub}>{fmt(sel.time_slot)} · {sel.party_size} guests · {sel.table_label}</div>
              </div>
              <button style={s.closeBtn} onClick={()=>setSel(null)}>×</button>
            </div>
            <div style={s.divider} />
            <dl style={s.grid}>
              {[['Confirmation',sel.confirmation_number],['Date',sel.date],['Time',fmt(sel.time_slot)],
                ['Party',sel.party_size+' guests'],['Table',sel.tables_display||sel.table_label],['Phone',sel.phone],
                ['Email',sel.email||'—'],['Status',sel.status.replace('_',' ')],['Source',sel.source],
                ['Booked on', fmtBookedOn(sel.created_at)],
                sel.special_occasion ? ['Occasion','🎉 '+sel.special_occasion] : null,
                ['Dietary',sel.dietary_restrictions||'—'],['Requests',sel.special_requests||'—'],['Notes',sel.notes||'—']]
              .filter(Boolean)
              .map(([k,v])=>(
                <React.Fragment key={k}><dt style={s.dt}>{k}</dt><dd style={s.dd}>{v}</dd></React.Fragment>
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  header:    { display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 },
  h1:        { fontSize:28, fontWeight:800, color:'var(--text-primary)', margin:0, letterSpacing:'-0.5px' },
  sub:       { fontSize:14, color:'var(--text-muted)', marginTop:4 },
  filterBar: { display:'flex', gap:10, marginBottom:12, flexWrap:'wrap', alignItems:'center' },
  fi:        { background:'var(--input-bg)', border:'1px solid var(--input-border)', borderRadius:8, padding:'8px 12px', fontSize:13, outline:'none', color:'var(--input-text)' },
  clearBtn:  { background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:13, textDecoration:'underline' },
  sortBar:   { display:'flex', alignItems:'center', gap:8, marginBottom:16 },
  sortLabel: { fontSize:12, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em' },
  sortBtn:   { background:'none', border:'1px solid #e2e8f0', borderRadius:7, padding:'5px 13px', fontSize:12, fontWeight:600, color:'var(--text-muted)', cursor:'pointer', transition:'all 0.15s' },
  sortBtnActive:{ background:'#4f46e5', borderColor:'#4f46e5', color:'#fff' },
  empty:     { color:'var(--text-muted)', textAlign:'center', padding:'60px 0', fontSize:15 },
  tableWrap: { background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:18, overflow:'hidden', boxShadow:'0 1px 4px rgba(15,23,42,0.06)' },
  table:     { width:'100%', borderCollapse:'collapse' },
  th:        { padding:'12px 16px', fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', textAlign:'left', background:'var(--section-bg)', borderBottom:'1px solid var(--section-border)' },
  td:        { padding:'13px 16px', fontSize:13, color:'var(--text-secondary)', borderBottom:'1px solid var(--divider)' },
  badge:     { display:'inline-flex', alignItems:'center', gap:5, fontWeight:600, fontSize:11, borderRadius:20, padding:'3px 9px' },
  dot:       { width:5, height:5, borderRadius:'50%', flexShrink:0 },
  aiTag:     { fontSize:12, color:'#4f46e5', fontWeight:600 },
  manTag:    { fontSize:12, color:'var(--text-muted)' },
  occasionTag:{ fontSize:13, cursor:'default' },
  overlay:   { position:'fixed', inset:0, background:'rgba(15,23,42,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 },
  modal:     { background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:22, padding:30, width:520, maxWidth:'90vw', maxHeight:'85vh', overflow:'auto', boxShadow:'0 20px 60px rgba(15,23,42,0.18)' },
  modalTop:  { display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18, gap:12 },
  modalName: { fontSize:20, fontWeight:800, color:'var(--text-primary)', margin:'0 0 4px' },
  modalSub:  { fontSize:13, color:'var(--text-muted)' },
  closeBtn:  { background:'var(--section-bg)', border:'1px solid var(--section-border)', borderRadius:8, width:32, height:32, fontSize:20, cursor:'pointer', color:'var(--text-secondary)', flexShrink:0 },
  divider:   { height:1, background:'var(--divider)', margin:'0 0 18px' },
  grid:      { display:'grid', gridTemplateColumns:'140px 1fr', gap:'11px 14px' },
  dt:        { fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' },
  dd:        { fontSize:13, color:'var(--text-secondary)' },
};
